import { useSearchParams } from "react-router";
import { useCallback } from "react";
import { cloneDeep } from "lodash-es";

import { setUpdatedFiltersUrlParams } from "components/Filters/helpers";
import { ActiveFiltersMap } from "components/Filters/types";
import { StackSuggestions } from "constants/stack";
import { SearchSuggestionsFieldType } from "types/generated";
import { URL_FILTER_TAB_KEY } from "constants/urlQueryKeys";
import useTypedContext from "hooks/useTypedContext";
import { FiltersContext } from "components/Filters";

import {
  FILTERS_ORDER_SETTINGS_KEY,
  PREDEFINED_FILTER_STATES,
  PredefinedFilterType,
} from "./constants";
import { getPredefinedFilterTab } from "./helpers";

const usePredefinedFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeFilters } = useTypedContext(FiltersContext);

  const predefinedFilterTab = getPredefinedFilterTab(searchParams);

  const handleUpdateUrlParams = useCallback(
    (activeFilters: ActiveFiltersMap, filterTabKey: string) => {
      if (filterTabKey) {
        searchParams.set(URL_FILTER_TAB_KEY, filterTabKey);
      } else {
        searchParams.delete(URL_FILTER_TAB_KEY);
      }

      const updatedParams = setUpdatedFiltersUrlParams(
        activeFilters,
        searchParams,
        sessionStorage,
        FILTERS_ORDER_SETTINGS_KEY
      );

      setSearchParams(updatedParams);
    },
    [searchParams, setSearchParams]
  );

  const setPredefinedFilter = useCallback(
    (type: PredefinedFilterType) => {
      if (type === predefinedFilterTab) {
        return;
      }

      const clonedActiveFilters = cloneDeep(activeFilters);

      if (type === PredefinedFilterType.AllStacks) {
        clonedActiveFilters.delete(StackSuggestions.State);
      } else {
        clonedActiveFilters.set(StackSuggestions.State, {
          key: StackSuggestions.State,
          filterName: StackSuggestions.State,
          type: SearchSuggestionsFieldType.Enum,
          values: [...PREDEFINED_FILTER_STATES[type]],
        });
      }

      handleUpdateUrlParams(clonedActiveFilters, type);
    },
    [handleUpdateUrlParams, predefinedFilterTab, activeFilters]
  );

  return {
    predefinedFilterTab,
    setPredefinedFilter,
  };
};

export default usePredefinedFilters;
